import { useElementSize } from '@mantine/hooks'
import { Stack, useTheme } from '@mui/material'
import { Suspense } from 'react'
import { useLocation } from 'react-router-dom'

import LoadingPaperSkeleton from '~/components/loadingPaperSkeleton'
import useGetElementDimensions from '~/features/ui/hooks/useGetElementDimensions'
import EElement from '~/features/ui/models'
import Header from '~/layouts/account/header'
import PageLayout from '~/layouts/page'
import { EClientPath } from '~/models/api'
import rem from '~/utils/ui'

type TProps = {
  children: React.ReactNode
  isLoading?: boolean
  noPadding?: boolean
}

function ContentWrapper({
  children,
  isLoading = false,
  noPadding = false,
}: TProps) {
  const location = useLocation()
  const { ref: headerRef, height: headerHeightUnPadded } = useElementSize()
  const fullHeaderHeight =
    headerRef?.current?.clientHeight > headerHeightUnPadded
      ? headerRef?.current?.clientHeight
      : headerHeightUnPadded
  const { width: sideNavWidth } = useGetElementDimensions(
    EElement.accountSideNav,
  )
  const theme = useTheme()

  const loadingTemplate = (
    <>
      {[
        EClientPath.reports,
        EClientPath.contacts,
        EClientPath.referralsInfo,
        EClientPath.integrations,
      ].some((path) => location.pathname.includes(path)) && (
        <LoadingPaperSkeleton sx={{ height: '3rem', mb: '5rem' }} />
      )}
      {!location.pathname.includes(EClientPath.inbox) && (
        <LoadingPaperSkeleton sx={{ height: '70vh' }} />
      )}
      {location.pathname.includes(EClientPath.inbox) && (
        <LoadingPaperSkeleton
          sx={{ height: `calc(100vh - ${fullHeaderHeight}px)` }}
        />
      )}
    </>
  )

  return (
    <Stack
      spacing={0}
      sx={{
        minHeight: '100vh',
        backgroundColor: theme.palette.mainBackground,
        paddingBottom: noPadding ? 0 : rem(55),
      }}
    >
      <Header ref={headerRef} />
      <Stack
        sx={{
          ml: { md: rem(sideNavWidth) },
          mt: rem(fullHeaderHeight),
        }}
      >
        <PageLayout
          containerProps={{
            sx: {
              minHeight: 'inherit',
              display: 'flex',
              alignItems: 'center',
              '&.MuiContainer-root': {
                paddingX: noPadding ? '0' : undefined,
                maxWidth: noPadding ? 'unset' : undefined,
              },
            },
          }}
          sx={{ minHeight: 'inherit' }}
        >
          <Stack
            spacing={3}
            sx={{
              width: '100%',
              minHeight: 'inherit',
              paddingBlock: noPadding ? 0 : { xxxs: 2, xs: 4, md: 8 },
            }}
          >
            {isLoading ? (
              loadingTemplate
            ) : (
              <Suspense fallback={loadingTemplate}>{children}</Suspense>
            )}
          </Stack>
        </PageLayout>
      </Stack>
    </Stack>
  )
}

export default ContentWrapper
